/**
 * Cart Add-on Guard
 * Runs globally (every page, via theme.liquid) and checks: if any cart line
 * item has a written personal card message but the matching paid surcharge
 * line item is missing (e.g. the customer deleted it), silently re-add it.
 *
 * Reads its config from localStorage, written by delivery-details-form.js
 * whenever a product page with the delivery block loads. If a customer
 * never visited such a page this session, there's nothing to guard yet.
 */
(function () {
  function getConfig() {
    try {
      var raw = localStorage.getItem('dd_addon_config');
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.enabled || !parsed.variantId) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function findPropertyValue(properties, matchFn) {
    if (!properties) return null;
    for (var key in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, key) && matchFn(key)) {
        return { key: key, value: properties[key] };
      }
    }
    return null;
  }

  function isMessageKey(key) {
    var k = key.toLowerCase();
    return k.indexOf('kortbesked') !== -1 || k.indexOf('card message') !== -1 || k.indexOf('besked') !== -1;
  }

  function isProtectedLineItem(cartItemEl) {
    // Our surcharge/add-on line items always carry a 'Hører til' property,
    // which Dawn renders as visible text within the line item's row/card.
    // Matching on that text is more robust than guessing exact class names,
    // since it works regardless of how heavily the cart template is customized.
    return cartItemEl.textContent.indexOf('Hører til') !== -1;
  }

  function lockProtectedLineItems() {
    // Try a few common Dawn selectors for "one cart line item" — first match wins per element
    var cartItems = document.querySelectorAll('.cart-item, [data-cart-item], tr.cart-item, li.cart-item');

    cartItems.forEach(function (item) {
      if (!isProtectedLineItem(item)) return;
      if (item.getAttribute('data-dd-locked') === 'true') return; // already processed

      // Hide/disable the quantity selector (Dawn: <quantity-input> wrapping +/- buttons and a number input)
      var quantitySelectors = [
        'quantity-input',
        '.quantity',
        '[name="updates[]"]',
        '.cart-item__quantity',
        '.quantity__input'
      ];
      quantitySelectors.forEach(function (sel) {
        item.querySelectorAll(sel).forEach(function (el) {
          el.style.visibility = 'hidden';
          if (el.tagName === 'INPUT' || el.tagName === 'BUTTON') {
            el.disabled = true;
          }
        });
      });

      // Hide/disable the remove button (Dawn: <cart-remove-button> wrapping a link/button)
      var removeSelectors = [
        'cart-remove-button',
        '.cart-remove-button',
        'a[href*="cart/change"][data-quantity="0"]',
        '[data-remove-item]'
      ];
      removeSelectors.forEach(function (sel) {
        item.querySelectorAll(sel).forEach(function (el) {
          el.style.visibility = 'hidden';
          el.style.pointerEvents = 'none';
        });
      });

      item.setAttribute('data-dd-locked', 'true');
    });
  }

  function extractLinkedParentInfo(cartItemEl) {
    // Pull both the linked product title AND the exact message text from the
    // surcharge line's rendered properties, e.g.:
    // "Hører til: Rosenbuket med grønt" and "Besked: Tillykke..."
    // Matching on BOTH (not just the title) keeps pairing correct even when
    // the cart has two line items with the same or similar product name.
    var text = cartItemEl.textContent;
    var titleMatch = text.match(/Hører til:?\s*([^\n\r]+)/);
    var messageMatch = text.match(/Besked:?\s*([^\n\r]+)/);
    if (!titleMatch) return null;
    return {
      title: titleMatch[1].trim(),
      message: messageMatch ? messageMatch[1].trim() : null
    };
  }

  function reorderProtectedLineItems() {
    var cartItems = Array.prototype.slice.call(
      document.querySelectorAll('.cart-item, [data-cart-item], tr.cart-item, li.cart-item')
    );
    if (cartItems.length === 0) return;

    var moved = false;
    var usedParents = []; // prevents two surcharges from both latching onto the same parent line

    cartItems.forEach(function (item) {
      if (!isProtectedLineItem(item)) return;

      var linkInfo = extractLinkedParentInfo(item);
      if (!linkInfo) return;

      var parentEl = cartItems.find(function (other) {
        if (other === item || isProtectedLineItem(other)) return false;
        if (usedParents.indexOf(other) !== -1) return false; // already claimed by another surcharge
        var titleMatches = other.textContent.indexOf(linkInfo.title) !== -1;
        if (!titleMatches) return false;
        // If we have the message text too, require it to also appear on the
        // parent line (that's where the customer's own message is echoed
        // back) for a precise, unambiguous match.
        if (linkInfo.message) {
          return other.textContent.indexOf(linkInfo.message) !== -1;
        }
        return true;
      });

      if (!parentEl) return;
      usedParents.push(parentEl);

      // Already directly after its parent? nothing to do.
      if (parentEl.nextElementSibling === item) return;

      parentEl.parentNode.insertBefore(item, parentEl.nextElementSibling);
      moved = true;
    });

    return moved;
  }

  function applyAddonPresentation() {
    // Guard against the MutationObserver re-triggering itself: disconnect
    // while we make DOM changes, then reconnect once settled.
    if (window.__ddObserver) window.__ddObserver.disconnect();

    reorderProtectedLineItems();
    lockProtectedLineItems();

    if (window.__ddObserver && window.__ddObserverTarget) {
      window.__ddObserver.observe(window.__ddObserverTarget, { childList: true, subtree: true });
    }
  }

  function findOrphanedSurcharges(cart) {
    // A surcharge line is orphaned if the product it was linked to (by exact
    // title + exact message text) no longer exists anywhere in the cart —
    // e.g. the customer deleted the flowers themselves, not just the surcharge.
    var orphanedLines = [];

    cart.items.forEach(function (item, index) {
      if (!item.properties || !item.properties['Hører til']) return; // not a surcharge line

      var parentTitle = item.properties['Hører til'];
      var parentMessage = item.properties['Besked'];

      var hasParent = cart.items.some(function (other) {
        if (other === item) return false;
        if (other.properties && other.properties['Hører til']) return false; // don't match another surcharge
        if (other.product_title !== parentTitle) return false;
        if (parentMessage) {
          var messageProp = findPropertyValue(other.properties, isMessageKey);
          return messageProp && messageProp.value === parentMessage;
        }
        return true;
      });

      if (!hasParent) {
        orphanedLines.push(index + 1); // Shopify's /cart/change.js `line` is 1-based
      }
    });

    return orphanedLines;
  }

  function removeLines(lineNumbers) {
    // Remove from highest line number to lowest, sequentially, so earlier
    // line numbers stay valid as later ones are removed
    var sorted = lineNumbers.slice().sort(function (a, b) { return b - a; });
    var chain = Promise.resolve();
    sorted.forEach(function (line) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: 0 })
        });
      });
    });
    return chain;
  }

  function checkAndRepair() {
    var config = getConfig();
    if (!config) return;

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var toRestore = [];

        cart.items.forEach(function (item) {
          // Skip the surcharge item itself (it has 'Hører til', not a message property)
          if (item.properties && item.properties['Hører til']) return;

          var messageProp = findPropertyValue(item.properties, isMessageKey);
          if (!messageProp || !messageProp.value || !messageProp.value.trim()) return;

          // This item has a written message. Does a matching surcharge line exist?
          var hasMatchingSurcharge = cart.items.some(function (other) {
            return other.properties &&
              other.properties['Hører til'] === item.product_title &&
              other.properties['Besked'] === messageProp.value;
          });

          if (!hasMatchingSurcharge) {
            toRestore.push({
              id: config.variantId,
              quantity: 1,
              properties: {
                'Besked': messageProp.value,
                'Hører til': item.product_title
              }
            });
          }
        });

        var orphanedLines = findOrphanedSurcharges(cart);

        if (toRestore.length === 0 && orphanedLines.length === 0) return;

        var work = Promise.resolve();

        if (orphanedLines.length > 0) {
          work = work.then(function () { return removeLines(orphanedLines); });
        }

        if (toRestore.length > 0) {
          work = work.then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: toRestore })
            });
          });
        }

        work.then(function () {
          // Refresh the visible cart so the changes show immediately
          if (window.location.pathname.indexOf('/cart') !== -1) {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        });
      })
      .catch(function () {
        // Silent — this is a background safeguard, not user-facing functionality
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    checkAndRepair();
    applyAddonPresentation();
  });

  // Also re-check whenever the cart drawer opens or a cart change event fires,
  // covering the case where someone removes the item via the drawer without
  // a full page reload
  document.addEventListener('cart:updated', function () {
    checkAndRepair();
    applyAddonPresentation();
  });
  document.addEventListener('cart:refresh', function () {
    checkAndRepair();
    applyAddonPresentation();
  });

  // Dawn's cart drawer re-renders its contents via AJAX (innerHTML swaps),
  // which won't fire the events above. Watch for that and re-apply.
  var cartObserverTarget = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer, #main-cart-items, cart-items');
  if (cartObserverTarget && window.MutationObserver) {
    window.__ddObserverTarget = cartObserverTarget;
    window.__ddObserver = new MutationObserver(function () {
      applyAddonPresentation();
    });
    window.__ddObserver.observe(cartObserverTarget, { childList: true, subtree: true });
  }
})();